<template>
  <div v-if="canShare">
    <hr>
    <h6>{{ $t('views.networks.modal.sharing-advanced') }}</h6>

    <!-- Groups -->
    <b-row>
      <b-col
        cols="12"
        xl="12"
      >
        <b-form-checkbox
          v-model="groupsChecked"
          :value="true"
          :unchecked-value="false"
        >
          {{ $t('forms.allowed.groups') }}
        </b-form-checkbox>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="12"
        xl="12"
      >
        <IsardSearchSelect
          :placeholder="placeholder"
          :disabled="!groupsChecked"
          :options="groupOptions"
          :selected-values="selectedGroups"
          :close-on-select="false"
          :deselect-from-dropdown="true"
          @search="searchGroups"
          @updateSelected="updateSelectedGroups"
        />
      </b-col>
    </b-row>

    <!-- Users -->
    <b-row class="mt-4">
      <b-col
        cols="12"
        xl="12"
      >
        <b-form-checkbox
          v-model="usersChecked"
          :value="true"
          :unchecked-value="false"
        >
          {{ $t('forms.allowed.users') }}
        </b-form-checkbox>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="12"
        xl="12"
      >
        <IsardSearchSelect
          :placeholder="placeholder"
          :disabled="!usersChecked"
          :options="userOptions"
          :selected-values="selectedUsers"
          :close-on-select="false"
          :deselect-from-dropdown="true"
          @search="searchUsers"
          @updateSelected="updateSelectedUsers"
        />
      </b-col>
    </b-row>

    <!-- Deployments -->
    <template v-if="showDeployments">
      <b-row class="mt-4">
        <b-col
          cols="12"
          xl="12"
        >
          <b-form-checkbox
            v-model="deploymentsChecked"
            :value="true"
            :unchecked-value="false"
          >
            {{ $t('forms.allowed.deployments') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-row v-if="deploymentsChecked">
        <b-col
          cols="12"
          xl="12"
        >
          <b-alert
            variant="warning"
            show
          >
            {{ $t('views.networks.modal.deployments-warning') }}
          </b-alert>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          xl="12"
        >
          <IsardSearchSelect
            :placeholder="placeholder"
            :disabled="!deploymentsChecked"
            :options="deploymentOptions"
            :selected-values="selectedDeployments"
            :close-on-select="false"
            :deselect-from-dropdown="true"
            @search="searchDeployments"
            @updateSelected="updateSelectedDeployments"
          />
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api'
import IsardSearchSelect from '@/components/shared/IsardSearchSelect.vue'
import { AllowedUtils } from '@/utils/allowedUtils'
import { apiV3Segment } from '@/shared/constants'
import axios from 'axios'
import i18n from '@/i18n'

export default {
  components: {
    IsardSearchSelect
  },
  props: {
    showDeployments: {
      type: Boolean,
      default: false
    }
  },
  setup (props, context) {
    const $store = context.root.$store
    const placeholder = `${i18n.t('forms.allowed.placeholder')} ${i18n.t('forms.allowed.empty')}`

    // Check if user can share (advanced/manager/admin roles)
    const canShare = computed(() => {
      const user = $store.getters.getUser
      return user && ['advanced', 'manager', 'admin'].includes(user.role_id)
    })

    // Local state for groups
    const groupsChecked = ref(false)
    const groupOptions = ref([])
    const selectedGroups = ref([])

    // Local state for users
    const usersChecked = ref(false)
    const userOptions = ref([])
    const selectedUsers = ref([])

    // Local state for deployments
    const deploymentsChecked = ref(false)
    const deploymentOptions = ref([])
    const selectedDeployments = ref([])

    // Search functions - make API calls directly
    const searchGroups = async (data) => {
      try {
        const response = await axios.post(`${apiV3Segment}/admin/allowed/term/groups`, {
          table: 'groups',
          term: data.search
        })
        groupOptions.value = AllowedUtils.parseGroups(response.data)
      } finally {
        data.loading(false)
      }
    }

    const searchUsers = async (data) => {
      try {
        const response = await axios.post(`${apiV3Segment}/admin/allowed/term/users`, {
          table: 'users',
          term: data.search
        })
        userOptions.value = AllowedUtils.parseUsers(response.data)
      } finally {
        data.loading(false)
      }
    }

    const searchDeployments = async (data) => {
      try {
        const response = await axios.post(`${apiV3Segment}/admin/allowed/term/deployments`, {
          table: 'deployments',
          term: data.search
        })
        deploymentOptions.value = AllowedUtils.parseItems(response.data)
      } finally {
        data.loading(false)
      }
    }

    // Update selection handlers
    const updateSelectedGroups = (selected) => {
      selectedGroups.value = selected
      emitAllowed()
    }

    const updateSelectedUsers = (selected) => {
      selectedUsers.value = selected
      emitAllowed()
    }

    const updateSelectedDeployments = (selected) => {
      selectedDeployments.value = selected
      emitAllowed()
    }

    // Build and emit allowed object
    const emitAllowed = () => {
      const allowed = {
        groups: groupsChecked.value ? selectedGroups.value.map(g => g.id) : false,
        users: usersChecked.value ? selectedUsers.value.map(u => u.id) : false,
        deployments: deploymentsChecked.value ? selectedDeployments.value.map(d => d.id) : false
      }
      context.emit('update:allowed', allowed)
    }

    // Reset selections when unchecking
    watch(groupsChecked, (checked) => {
      if (!checked) {
        selectedGroups.value = []
        groupOptions.value = []
      }
      emitAllowed()
    })

    watch(usersChecked, (checked) => {
      if (!checked) {
        selectedUsers.value = []
        userOptions.value = []
      }
      emitAllowed()
    })

    watch(deploymentsChecked, (checked) => {
      if (!checked) {
        selectedDeployments.value = []
        deploymentOptions.value = []
      }
      emitAllowed()
    })

    return {
      canShare,
      placeholder,
      // Groups
      groupsChecked,
      groupOptions,
      selectedGroups,
      searchGroups,
      updateSelectedGroups,
      // Users
      usersChecked,
      userOptions,
      selectedUsers,
      searchUsers,
      updateSelectedUsers,
      // Deployments
      deploymentsChecked,
      deploymentOptions,
      selectedDeployments,
      searchDeployments,
      updateSelectedDeployments
    }
  }
}
</script>
